import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, Gift, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, money } from "@/lib/api";
import { FILING_FEE, planAmounts } from "@/lib/constants";

const Option = ({ active, onClick, title, price, sub, badge, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`relative flex-1 rounded-lg border p-4 text-left transition-colors duration-150 ${active ? "border-blue-700 bg-blue-50 ring-1 ring-blue-700" : "border-slate-200 bg-white hover:border-slate-400"}`}
  >
    {badge && <span className="absolute right-3 top-3 rounded-full bg-emerald-600 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase text-white">{badge}</span>}
    <p className="text-sm font-semibold text-slate-900">{title}</p>
    <p className="mt-1 font-mono text-xl font-semibold">{price}</p>
    <p className="text-xs text-slate-500">{sub}</p>
  </button>
);

export const PlanPicker = ({ quote }) => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [plan, setPlan] = useState("monthly");
  const [busy, setBusy] = useState(false);
  const m = quote.monthly_premium;
  const fee = quote.filing_fee ?? FILING_FEE;
  const full = planAmounts(m, "full", fee);
  const due = planAmounts(m, plan, fee).dueToday;

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { quote_id: quote.id, origin_url: window.location.origin, plan });
      if (data.policy_id) {
        toast.success("Policy issued at no charge");
        nav(`/policies/${data.policy_id}`);
        return;
      }
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(false);
    }
  };

  if (user?.role === "admin") {
    return (
      <div className="mt-4 border-t border-slate-100 pt-4" data-testid={`plan-picker-${quote.id}`}>
        <p className="mb-3 text-sm text-slate-600">Owner account: all products are free for you.</p>
        <Button onClick={pay} disabled={busy} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="admin-issue-self-btn">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Gift className="mr-2 h-4 w-4" />} Issue policy at no charge
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-4 border-t border-slate-100 pt-4" data-testid={`plan-picker-${quote.id}`}>
      <p className="mb-3 text-sm font-semibold">Choose how to pay</p>
      <div className="flex flex-col gap-3 sm:flex-row">
        <Option testId="plan-option-monthly" active={plan === "monthly"} onClick={() => setPlan("monthly")} title="Monthly auto-pay" price={`${money(m)}/mo`} sub="Card charged automatically each month for 12 months" />
        <Option testId="plan-option-full" active={plan === "full"} onClick={() => setPlan("full")} title="Pay in full" price={money(full.total)} sub={`One payment for 12 months · save ${money(full.savings)}`} badge="Save 10%" />
      </div>
      <div className="mt-4 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <p className="text-sm text-slate-600" data-testid={`due-today-${quote.id}`}>
          Due today: <span className="font-mono font-semibold text-slate-900">{money(due)}</span> <span className="text-xs text-slate-500">{fee ? `(incl. $${fee} state filing fee) · ` : ""}Debit or credit card</span>
        </p>
        <Button onClick={pay} disabled={busy} className="bg-emerald-600 text-white hover:bg-emerald-700 hover:text-white" data-testid="stripe-checkout-pay-btn">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />} Pay {money(due)} & activate
        </Button>
      </div>
    </div>
  );
};
